import React from 'react';

import {
  Radio,
  RadioGroup,
  Typography,
  FormControlLabel,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Button,
  DialogTitle,
  Link,
  DialogActions,
} from '@bessemercws/components/bui';
import { ElectionDescValue, IElectionDescValue } from '@bessemercws/graphql';
import { ElectronicDeliveryForm } from '@bessemercws/preferences';
import { useSnackbarContext } from '@bessemercws/utils';
import { Controller, useForm } from 'react-hook-form';

import {
  consentBody,
  consentBodyThree,
  consentBodyTwo,
  consentButton,
  deliveryBody,
  preferenceDialogTitle,
  subTitle,
  warningBody,
} from '#/constants/userVerificationConstant';
import { StyledDialogContent } from './DeliveryCard.style';
import { useUserVerificationContext } from '../UserVerificationContext/UserVerificationContext';

export interface OtherUserDeliveryPreferences {
  name: string;
  electronicDeliveryOption: ElectionDescValue;
}

export const DeliveryCard = () => {
  const { handleUpdateElecDelivery, otherAuthorizedUserDetails } = useUserVerificationContext();
  const { control, getValues, handleSubmit } = useForm<ElectronicDeliveryForm>({
    defaultValues: { selection: undefined },
  });
  const { handleSetSnackbarMessage } = useSnackbarContext();

  const tempDeliveryOption = getValues('selection');

  const doesOneAuthUserHaveDiffSelection = (otherAuthorizedUserDetails || []).some(
    (item) => item.otherAuthorizedUserDelPref !== tempDeliveryOption,
  );
  const getOptionLabel = (option: ElectionDescValue | null | undefined): string => {
    if (!option) return '--';
    if (option === ElectionDescValue.Online) return 'Electronic only';
    return 'Electronic & paper';
  };

  const handleChangeDeliveryOption = async (data: ElectronicDeliveryForm) => {
    if (!data.selection) {
      handleSetSnackbarMessage('selection not found');
      return;
    }
    handleUpdateElecDelivery(data.selection);
  };

  return (
    <>
      <DialogTitle> {preferenceDialogTitle} </DialogTitle>
      <StyledDialogContent className="dialogContent" data-testid="delivery-card-content">
        <Typography variant="body3" className="deliveryPreference">
          {deliveryBody}
        </Typography>
        <form className="radio-buttons">
          <Controller
            name="selection"
            control={control}
            render={({ field }) => (
              <RadioGroup row {...field} data-testid="radio-group">
                <FormControlLabel
                  value={IElectionDescValue.Online}
                  control={<Radio />}
                  label={getOptionLabel(ElectionDescValue.Online)}
                  typographyProps={{ variant: 'body2' }}
                  data-testid="electronic-radio-button"
                />
                <FormControlLabel
                  value={IElectionDescValue.OnlineAndPaper}
                  control={<Radio />}
                  label={getOptionLabel(ElectionDescValue.OnlineAndPaper)}
                  typographyProps={{ variant: 'body2' }}
                  data-testid="electronic-and-paper-radio-button"
                />
              </RadioGroup>
            )}
          />
        </form>

        {(otherAuthorizedUserDetails || []).length > 0 && (
          <>
            <Alert
              severity="warning"
              open={doesOneAuthUserHaveDiffSelection}
              title="Important"
              className="otherUsers-warning"
            >
              {warningBody}
            </Alert>
            <div className="otherUsers-container">
              <Typography
                className="section-header"
                color="textSecondary"
                weight="medium"
                component="div"
                variant="body3"
              >
                {subTitle}
              </Typography>
              <Table data-testid="otherUsers-table">
                <TableBody>
                  {otherAuthorizedUserDetails.map((user) => {
                    return (
                      <TableRow key={user.userId}>
                        <TableCell scope="row" colSpan={2} align="left">
                          <div className="userid-cell">{user.name}</div>
                        </TableCell>
                        <TableCell className="userSelection-cell">
                          {getOptionLabel(user.otherAuthorizedUserDelPref)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        <Typography variant="body3" className="consent-text">
          {consentBody}{' '}
          <Link
            target="_blank"
            href="/assets/Bessemer_CWS_E-delivery Consent Agreement_Final_022924.pdf"
            underline="always"
            rel="noreferrer"
            variant="body3"
          >
            {consentBodyTwo}
          </Link>
          {consentBodyThree}
        </Typography>
      </StyledDialogContent>
      <DialogActions>
        <Button size="medium" onClick={handleSubmit(handleChangeDeliveryOption)}>
          {' '}
          {consentButton}
        </Button>
      </DialogActions>
    </>
  );
};
